import React from "react";
import styled from "styled-components";
import { CostCalculator } from "../landing/Interactive";
import { Container, Eyebrow, Display, Lead, useReveal } from "../landing/ui";
import { colors, fonts, media } from "../../styles/theme";

// Four cells in two rows: the two results in dark cards on top, the speed comparison and
// the savings calculator beneath. Rows align by construction, so the cards cannot drift
// out of step when the copy changes. The lower row absorbs the spare height and drops its
// closing element to the foot, so neither column ends in dead space.

const Head = styled.div`
  max-width: 760px;
  margin-bottom: 44px;
`;

const Quad = styled.div`
  display: grid;
  gap: 24px;

  @media ${media.medium} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  > :nth-child(3),
  > :nth-child(4) {
    display: flex;
    flex-direction: column;
  }
  > :nth-child(3) > p:last-of-type,
  > :nth-child(4) > div:last-of-type {
    margin-top: auto;
  }
`;

const Headline = styled.div`
  border-radius: 28px;
  padding: 28px;
  background: ${colors.forest};
  border: 1px solid rgba(250, 247, 241, 0.12);
  color: ${colors.linen};

  @media ${media.medium} {
    padding: 40px;
  }

  h3 {
    font-family: ${fonts.mono};
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: ${colors.sage};
  }
  b {
    display: block;
    margin: 16px 0 10px;
    font-family: ${fonts.display};
    font-weight: 300;
    font-size: clamp(44px, 5vw, 68px);
    line-height: 1;
    letter-spacing: -0.04em;
    color: ${colors.copperLight};
  }
  p {
    font-family: ${fonts.body};
    font-size: 17px;
    line-height: 1.5;
    color: rgba(250, 247, 241, 0.8);
  }
  small {
    display: block;
    margin-top: 14px;
    font-family: ${fonts.mono};
    font-size: 10px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: rgba(250, 247, 241, 0.55);
  }
`;

// Multiples rather than percentages: they read harder at this scale, and there are no bars
// to cap out at 100% when the real figure is 406%.
const Speed = styled.div`
  border-radius: 28px;
  padding: 28px;
  background: ${colors.linen};
  border: 1px solid ${colors.stone};

  @media ${media.medium} {
    padding: 40px;
  }

  h3 {
    font-family: ${fonts.mono};
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: ${colors.pebble};
  }
  .lead {
    display: block;
    margin: 14px 0 4px;
    font-family: ${fonts.display};
    font-weight: 300;
    font-size: clamp(40px, 4.4vw, 60px);
    line-height: 1;
    letter-spacing: -0.04em;
    color: ${colors.ink};
  }
  .under {
    font-family: ${fonts.body};
    font-size: 16px;
    color: ${colors.bark};
  }
  ul {
    margin-top: 28px;
    border-top: 1px solid ${colors.sand};
  }
  li {
    display: flex;
    align-items: baseline;
    gap: 18px;
    padding: 16px 0;
    border-bottom: 1px solid ${colors.sand};
  }
  li b {
    flex: none;
    width: 86px;
    font-family: ${fonts.display};
    font-weight: 400;
    font-size: 34px;
    line-height: 1;
    letter-spacing: -0.03em;
    color: ${(p) => p.$accent};
  }
  li span {
    font-family: ${fonts.body};
    font-size: 16px;
    line-height: 1.45;
    color: ${colors.ink};
  }
  li small {
    display: block;
    font-family: ${fonts.mono};
    font-size: 10px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: ${colors.pebble};
  }
  > p {
    margin-top: 20px;
    font-family: ${fonts.body};
    font-size: 13px;
    color: ${colors.pebble};
  }
`;

const ResultsQuad = ({ data, accent = colors.terracotta, scope }) => {
  useReveal(scope);
  const { headlines, speed, eyebrow, titleLead, titleEmphasis, intro } = data;

  return (
    <Container>
      <Head>
        <Eyebrow $color={accent}>{eyebrow}</Eyebrow>
        <Display id="v-results" style={{ margin: "18px 0 20px" }} data-reveal>
          {titleLead} <em>{titleEmphasis}</em>
        </Display>
        {intro && <Lead data-reveal="0.08">{intro}</Lead>}
      </Head>
      <Quad>
        {headlines.map((h, i) => (
          <Headline key={h.title} data-reveal={i * 0.08}>
            <h3>{h.title}</h3>
            <b>{h.value}</b>
            <p>{h.body}</p>
            <small>{h.note}</small>
          </Headline>
        ))}

        <Speed $accent={accent} data-reveal="0.16">
          <h3>{speed.title}</h3>
          <span className="lead">{speed.value}</span>
          <span className="under">{speed.under}</span>
          <ul>
            {speed.rows.map((r) => (
              <li key={r.label}>
                <b>{r.multiple}</b>
                <span>
                  {r.label}
                  <small>{r.rate}</small>
                </span>
              </li>
            ))}
          </ul>
          <p>{speed.note}</p>
        </Speed>

        <CostCalculator accent={accent} />
      </Quad>
    </Container>
  );
};

export default ResultsQuad;
